/**
 * MemberCollectPayment — Phase 38 "Collect payment" action for the Member
 * profile. Full-clear ONLY (no partial allocation on this surface, per
 * locked CONTEXT): gathers ALL of this member's non-Paid plan-schedule
 * periods (via the contactId field added in 38-01), sorts oldest-first, and
 * mounts the shared RecordPaymentDialog (extracted in this same plan) with
 * the oldest period as `period` and every remaining one as
 * `additionalPeriods` — so ONE Collect action clears the member's ENTIRE
 * outstanding, matching the aggregate the profile's "Collect ₹{amount}"
 * button/stat tile shows, never just the oldest period.
 *
 * Data-fetch mirrors account-detail.tsx's PlanSchedule composition
 * (accountId-scoped memberships/deposits/credit-notes — inherited, not a
 * regression: deposits/credit notes have no contactId of their own today).
 */
import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetAccountPlanSchedule,
  useListAccountMemberships,
  useGetAccountDeposits,
  useListAccountCreditNotes,
  getGetAccountPlanScheduleQueryKey,
  getListAccountMembershipsQueryKey,
  getGetAccountDepositsQueryKey,
  getListAccountCreditNotesQueryKey,
  getGetContactStatsQueryKey,
  getGetContactTransactionsQueryKey,
  type Membership,
} from "@workspace/api-client-react";
import { RecordPaymentDialog } from "@/components/billing/record-payment-dialog";
import { isPeriodPayable } from "@/lib/period-payable";

export function MemberCollectPayment({
  accountId,
  contactId,
  open,
  onClose,
}: {
  accountId: number;
  contactId: number;
  /** Only fetches/renders while true — mirrors new-booking-sheet.tsx's
   *  `enabled: open` gating pattern for a conditionally-mounted dialog. */
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  const { data: scheduleData } = useGetAccountPlanSchedule(accountId, {
    query: { enabled: open, queryKey: getGetAccountPlanScheduleQueryKey(accountId) },
  });
  // Per-membership held deposit → so Record Payment on the member's oldest
  // pending period can show the receipt's full "rent + one-time deposit"
  // total when that period is its membership's index-0 period (mirrors
  // account-detail.tsx's PlanSchedule composition).
  const { data: memberships } = useListAccountMemberships(accountId, {
    query: { enabled: open, queryKey: getListAccountMembershipsQueryKey(accountId) },
  });
  const depositByMembership = useMemo(() => {
    const m = new Map<number, number>();
    for (const ms of (memberships ?? []) as Membership[]) {
      if (ms.securityDepositHeld != null) m.set(ms.id, Number(ms.securityDepositHeld));
    }
    return m;
  }, [memberships]);

  // Held deposits / Issued+Adjusted credit notes are ACCOUNT-scoped (no
  // contactId of their own) — every one on the account is offered as a
  // funding source, same inherited behavior as account-detail.tsx today.
  const { data: heldDepositsData } = useGetAccountDeposits(accountId, undefined, {
    query: { enabled: open, queryKey: getGetAccountDepositsQueryKey(accountId, undefined) },
  });
  const heldDeposits = heldDepositsData?.items ?? [];
  const { data: accountCreditNotes } = useListAccountCreditNotes(accountId, {
    query: { enabled: open, queryKey: getListAccountCreditNotesQueryKey(accountId) },
  });
  const applicableCreditNotes = (accountCreditNotes ?? []).filter(
    (cn) => cn.settlementStatus === "Issued" || cn.settlementStatus === "Adjusted",
  );

  // This member's own pending periods (contactId + non-Paid), oldest-first —
  // the "clear the FULL outstanding" set the Collect button targets.
  const pendingPeriods = useMemo(() => {
    const mine = (scheduleData?.periods ?? []).filter(
      // Future cycles aren't payable yet (server rejects them) — leave them out.
      (p) => p.contactId === contactId && p.status !== "Paid" && isPeriodPayable(p.periodStart),
    );
    return [...mine].sort((a, b) => {
      const byDate = a.periodStart.localeCompare(b.periodStart);
      return byDate !== 0 ? byDate : a.id - b.id;
    });
  }, [scheduleData, contactId]);

  const [oldest, ...additionalPeriods] = pendingPeriods;

  function handleClose() {
    // Refresh the profile's stat tiles + plan-schedule + transactions so
    // Pending Dues drops to 0 and the tiles/lists reflect the just-cleared
    // periods without a manual page refresh.
    queryClient.invalidateQueries({ queryKey: getGetContactStatsQueryKey(contactId) });
    queryClient.invalidateQueries({ queryKey: getGetContactTransactionsQueryKey(contactId) });
    queryClient.invalidateQueries({ queryKey: getGetAccountPlanScheduleQueryKey(accountId) });
    onClose();
  }

  // Empty state — nothing pending for this member (or not open yet): render
  // nothing rather than an empty/disabled dialog.
  if (!open || !oldest) return null;

  return (
    <RecordPaymentDialog
      accountId={accountId}
      period={oldest}
      additionalPeriods={additionalPeriods}
      firstPeriodDeposit={
        oldest.periodIndex === 0 ? depositByMembership.get(oldest.membershipId) ?? 0 : 0
      }
      heldDeposits={heldDeposits}
      creditNotes={applicableCreditNotes}
      onClose={handleClose}
    />
  );
}
